import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSortModule, MatSort, Sort } from '@angular/material/sort';
import { MatPaginatorModule, MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { Router, RouterModule } from '@angular/router';
import { of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { MessageDto } from '../../../models/api/messageDto';
import { formatRecipients } from '../../../utilities/message-recipients';
import { MessageService } from '../../../services/message.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { ComposeMessageDialogComponent } from '../compose-message-dialog/compose-message-dialog.component';
import { DatePipe } from '@angular/common';

type Mailbox = 'inbox' | 'sent';

@Component({
  selector: 'app-message-list',
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatIconModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatTooltipModule,
    NgxSpinnerModule,
    DatePipe,
    RouterModule
  ],
  templateUrl: './message-list.component.html',
  styleUrls: ['./message-list.component.scss']
})
export class MessageListComponent implements OnInit {
  dataSource = new MatTableDataSource<MessageDto>([]);
  totalItems = 0;
  pageSize = 20;
  pageNumber = 1;
  sortBy = 'CreatedDate';
  ascending = false;
  activeMailbox: Mailbox = 'inbox';

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  get displayedColumns(): string[] {
    return this.activeMailbox === 'inbox'
      ? ['title', 'sentByUserFullName', 'companyName', 'createdDate', 'actions']
      : ['title', 'recipients', 'companyName', 'createdDate', 'actions'];
  }

  constructor(
    private messageService: MessageService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService,
    private confirmationDialogService: ConfirmationDialogService,
    private dialog: MatDialog,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadMessages();
  }

  onMailboxChange(mailbox: Mailbox): void {
    this.activeMailbox = mailbox;
    this.pageNumber = 1;
    this.dataSource.data = [];
    this.loadMessages();
  }

  loadMessages(): void {
    this.spinnerService.show();
    const request$ = this.activeMailbox === 'inbox'
      ? this.messageService.getMessages(this.pageNumber, this.pageSize, this.sortBy, this.ascending)
      : this.messageService.getSentMessages(this.pageNumber, this.pageSize, this.sortBy, this.ascending);

    request$.pipe(
      map(response => {
        this.totalItems = response.totalCount;
        this.spinnerService.hide();
        return response.data;
      }),
      catchError(error => {
        console.error('Error loading messages:', error);
        this.spinnerService.hide();
        this.toast.error('Error loading messages', {
          iconTheme: { primary: 'var(--mat-sys-error)', secondary: '#FFF' }
        });
        return of([]);
      })
    ).subscribe(messages => {
      this.dataSource.data = messages;
    });
  }

  onComposeMessage(): void {
    const dialogRef = this.dialog.open(ComposeMessageDialogComponent, {
      width: '600px',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadMessages();
      }
    });
  }

  onSortChange(sort: Sort): void {
    if (sort.direction) {
      this.sortBy = sort.active;
      this.ascending = sort.direction === 'asc';
      this.loadMessages();
    }
  }

  onPageChange(event: PageEvent): void {
    this.pageNumber = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loadMessages();
  }

  onRowClick(message: MessageDto): void {
    const mailbox = this.activeMailbox;
    if (message.isAnswer) {
      this.router.navigate(['/messages', message.parentMessageId], {
        queryParams: { answerId: message.id, mailbox }
      });
    } else {
      this.router.navigate(['/messages', message.id], {
        queryParams: { originalOnly: true, mailbox }
      });
    }
  }

  onToggleReadStatus(message: MessageDto, event: Event): void {
    event.stopPropagation();
    const newStatus = !message.isRead;
    this.messageService.updateReadStatus(message.id, { isRead: newStatus }).subscribe({
      next: () => {
        message.isRead = newStatus;
        this.messageService.unreadCount.update(count =>
          Math.max(0, newStatus ? count - 1 : count + 1)
        );
      },
      error: (error) => {
        console.error('Error updating read status:', error);
        this.toast.error('Error updating read status', {
          iconTheme: { primary: 'var(--mat-sys-error)', secondary: '#FFF' }
        });
      }
    });
  }

  onMarkAllAsRead(): void {
    this.spinnerService.show();
    this.messageService.markAllAsRead().subscribe({
      next: () => {
        this.spinnerService.hide();
        this.messageService.refreshUnreadCount();
        this.loadMessages();
        this.toast.success('All messages marked as read', {
          iconTheme: { primary: 'var(--mat-sys-primary)', secondary: '#FFF' }
        });
      },
      error: (error) => {
        this.spinnerService.hide();
        console.error('Error marking all messages as read:', error);
        this.toast.error('Error marking all messages as read', {
          iconTheme: { primary: 'var(--mat-sys-error)', secondary: '#FFF' }
        });
      }
    });
  }

  onDeleteClick(message: MessageDto, event: Event): void {
    event.stopPropagation();
    this.confirmationDialogService.openConfirmationDialog(
      'Delete Message',
      `Are you sure you want to delete the message "${message.title}"?`,
      'Delete',
      'Cancel'
    ).subscribe(confirmed => {
      if (confirmed) {
        this.spinnerService.show();
        this.messageService.deleteMessage(message.id).subscribe({
          next: () => {
            this.spinnerService.hide();
            this.toast.success('Message deleted successfully', {
              iconTheme: { primary: 'var(--mat-sys-primary)', secondary: '#FFF' }
            });
            this.loadMessages();
          },
          error: (error) => {
            this.spinnerService.hide();
            this.toast.error('Error deleting message', {
              iconTheme: { primary: 'var(--mat-sys-error)', secondary: '#FFF' }
            });
            console.error('Error deleting message:', error);
          }
        });
      }
    });
  }

  getRecipientNames(message: MessageDto): string {
    return formatRecipients(message.recipientUsers, message.recipientRoles);
  }
}
